import {
  GridColDef,
  gridColumnFieldsSelector,
  GridColumnResizeParams,
} from '@mui/x-data-grid-premium'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React from 'react'

const useColumnsState = (
  apiRef: React.RefObject<GridApiPro | null>,
  columns: GridColDef[],
) => {
  const [widths, setWidths] = React.useState<
    Record<GridColDef['field'], number>
  >({})

  // Initialize orderedFields only once with the initial column fields
  const [orderedFields, setOrderedFields] = React.useState<
    GridColDef['field'][]
  >(() => columns.map((column) => column.field))

  // Update orderedFields when columns change (but only if the fields actually changed)
  React.useEffect(() => {
    const newFields = columns.map((column) => column.field)

    // Only update if the column set has changed (added/removed columns)
    setOrderedFields((currentFields) => {
      // Check if the sets of fields are different (columns added or removed)
      const fieldsChanged =
        newFields.length !== currentFields.length ||
        [...newFields].sort().join(',') !== [...currentFields].sort().join(',')

      if (fieldsChanged) {
        const newFieldsSet = new Set(newFields)
        const currentFieldsSet = new Set(currentFields)
        // Preserve the order of existing fields and append new ones
        const orderedExisting = currentFields.filter((field) =>
          newFieldsSet.has(field),
        )
        const newFieldsToAdd = newFields.filter(
          (field) => !currentFieldsSet.has(field),
        )
        return [...orderedExisting, ...newFieldsToAdd]
      }

      return currentFields
    })
  }, [columns])

  const onColumnWidthChange = React.useCallback(
    ({ colDef, width }: GridColumnResizeParams) => {
      setWidths((prev) => ({ ...prev, [colDef.field]: width }))
    },
    [],
  )

  const onColumnOrderChange = React.useCallback(() => {
    if (!apiRef.current) {
      return
    }
    const newFields = gridColumnFieldsSelector({ current: apiRef.current })
    setOrderedFields(newFields)
  }, [apiRef])

  const computedColumns = React.useMemo(
    () =>
      orderedFields.reduce<GridColDef[]>((acc, field) => {
        const column = columns.find((col) => col.field === field)
        if (!column) {
          return acc
        }

        // Apply saved width if it exists
        if (widths[field]) {
          acc.push({
            ...column,
            // see https://github.com/mui/mui-x/issues/4310#issuecomment-1082714528
            // handles flex for initial widths, and then removes it
            flex: undefined,
            width: widths[field],
          })
        } else {
          acc.push(column)
        }
        return acc
      }, []),
    [columns, widths, orderedFields],
  )

  // Memoize the return value to prevent unnecessary re-renders
  return React.useMemo(
    () => ({
      columns: computedColumns,
      onColumnWidthChange,
      onColumnOrderChange,
    }),
    [computedColumns, onColumnWidthChange, onColumnOrderChange],
  )
}

export default useColumnsState
